import * as Sentry from "@sentry/browser";
import { API_BASE_URL } from "config/env";
import Decimal from "decimal.js";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { getIsUserConnected } from "utils/userUtils";
import { Address, Hash } from "viem";
import { NUM_TRADES_TO_FETCH } from "../../config/consts";
import { Amount } from "../../types/amount";
import {
  HistoryMetadataRaw,
  TokenHistoryInfo,
  TokenHistoryInfoResponse,
  TradeHistoryData,
  TradeHistoryDataRaw,
  TransactionDetail,
} from "../../types/history";
import { ActivityStore } from "./ActivityStore";
import { Chain } from "viem/chains";

export const fetchAllHistory = (
  address: Address,
  timestamp: number,
  abortFetchSignal: AbortSignal
): Promise<
  | {
      metadata: HistoryMetadataRaw;
      nextAvailableTimestamp: number | null;
      tradeDataResults: Record<string, TransactionDetail>;
    }
  | Error
> => {
  /* The start & end parameters can be quite confusing:
   *   start=0 means start from the oldest possible point
   *   end= Today's timestamp meaning fetch tades from the oldest point to now
   * */

  return fetch(
    `${API_BASE_URL}/history/v2/trades?wallet_address=${address}&start=1&end=${timestamp}&size=${NUM_TRADES_TO_FETCH}`,
    { signal: abortFetchSignal }
  )
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        console.error(`Could not fetch trade history. `, response.statusText);
        Sentry.captureException(`Could not fetch trade history`, {
          extra: { error: response },
        });
        throw new Error("Trade history cannot be fetched.");
      }
    })
    .then((json: TradeHistoryDataRaw) => {
      const tradeDataResults: {
        result: TradeHistoryData["results"];
        metadata: HistoryMetadataRaw;
      } = transformTradeDataTypes(json);
      return {
        tradeDataResults: tradeDataResults.result,
        metadata: tradeDataResults.metadata,
        nextAvailableTimestamp: json.nextAvailableTimestamp,
      };
    })
    .catch(error => {
      return error as Error;
    });
};

export const getHistory = createAsyncAction(
  async ({
    address,
    timestamp,
    previousResult,
  }: {
    address: Address;
    timestamp: number;
    previousResult: TradeHistoryData | undefined;
  }) => {
    ActivityStore.update(s => {
      s.history.isLoading = true;
    });
    try {
      const controller = new AbortController();
      setTimeout(() => {
        controller.abort("History fetching is taking too long.");
      }, 10_000);

      let result:
        | null
        | {
            metadata: HistoryMetadataRaw;
            nextAvailableTimestamp: number | null;
            tradeDataResults: Record<string, TransactionDetail>;
          }
        | Error = null;
      for (let i = 0; i < 2; i++) {
        result = await fetchAllHistory(address, timestamp, controller.signal);
        if (!(result instanceof Error) && result != null) {
          break;
        }
      }
      if (result instanceof Error) {
        throw result;
      }
      if (result == null) {
        throw new Error("Trade history result is returning null or undefined.");
      }
      if (!(result instanceof Error) && result !== null && result.metadata) {
        const metadata = result.metadata;
        ActivityStore.update(s => {
          if (result) {
            // Augment the store map with the new metadata
            Object.keys(metadata.tokens).forEach(chainId => {
              s.historyMetadata[Number(chainId)] = {
                ...s.historyMetadata[Number(chainId)],
                ...metadata.tokens[Number(chainId)],
              };
            });
          }
        });
      }
      const results = previousResult
        ? { ...previousResult.results, ...result.tradeDataResults }
        : result.tradeDataResults;

      const newHistory = {
        results: results,
        nextAvailableTimestamp: result.nextAvailableTimestamp,
      } as TradeHistoryData;

      if (result.nextAvailableTimestamp !== undefined && result.nextAvailableTimestamp !== null) {
        void getHistory.run({
          address: address,
          timestamp: result.nextAvailableTimestamp,
          previousResult: newHistory,
        });
      }
      return successResult(newHistory);
    } catch (e) {
      console.error(e);
      const error = typeof e === "string" ? new Error(e) : e;
      if (error instanceof Error) {
        ActivityStore.update(s => {
          s.history.error = e as Error;
        });
      }
      Sentry.captureException(`Could not get trade history for network `, { extra: { event: e } });
      return errorResult([], `Could not get trade history for network ${address}`, e);
    }
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error && getIsUserConnected()) {
        ActivityStore.update(s => {
          s.history.data = result.payload;
        });
      }
      if (result.payload?.nextAvailableTimestamp == null) {
        ActivityStore.update(s => {
          s.history.isLoading = false;
        });
      }
    },
  }
);

export const uint256toAmountType = (amount: bigint, tokenDecimals: number): Amount => {
  const divisor = new Amount((10 ** tokenDecimals).toString());
  return new Amount(amount.toString()).div(divisor);
};
const transformTokens = (
  tokens: TokenHistoryInfoResponse,
  metadata: TradeHistoryDataRaw["metadata"],
  chainId: Chain["id"]
): TokenHistoryInfo => {
  const parsedTokens: TokenHistoryInfo = {};
  for (const tokenAddress of Object.keys(tokens)) {
    const tokenInfo = tokens[tokenAddress as Hash];
    parsedTokens[tokenAddress as Hash] = {
      address: tokenAddress as Hash,
      amount: tokenInfo
        ? uint256toAmountType(
            tokenInfo.amount,
            metadata.tokens[chainId][tokenAddress as Hash].decimals
          )
        : new Amount("0"),
      amountUsd: tokenInfo?.amountUsd ?? new Decimal(0),
    };
  }

  return parsedTokens;
};

export const transformTradeDataTypes = (rawData: TradeHistoryDataRaw) => {
  const result: TradeHistoryData["results"] = {};
  rawData.results.forEach(trade => {
    let volumeUsd: Amount = new Amount("0");
    try {
      volumeUsd = new Amount(trade.volumeUsd?.toString() ?? "0");
    } catch (e) {
      console.error(e);
      Sentry.captureException(`Could not get volumeUsd for ${trade.txHash} - will default to 0.`, {
        extra: { event: e },
      });
    }
    const metadata = rawData.metadata;
    const parsedTrade: TransactionDetail = {
      chain_id: trade.chain_id,
      status: trade.status,
      type: trade.type,
      sender: trade.taker,
      receiver: trade.receiver,
      sellTokens: transformTokens(trade.sellTokens, metadata, trade.chain_id),
      buyTokens: transformTokens(trade.buyTokens, metadata, trade.chain_id),
      timestamp: new Date(trade.timestamp),
      volumeUsd,
      gasFeeUsd: trade.gasFeeUsd,
      txHash: trade.txHash,
    };
    result[parsedTrade.txHash] = parsedTrade;
  });
  return { result, metadata: rawData.metadata };
};
